/**
 * Layar **sesi kas** — buka & tutup sesi, plus settlement (tutup toko).
 *
 * ### ⭐⭐ Hitungan kas BUTA — dan ini keputusan kontrol, bukan tata letak
 * `expected_cash` **tidak ditampilkan** sebelum kasir mengetik hitungannya. Kalau angka yang
 * "seharusnya" terlihat lebih dulu, kasir akan mengetik angka itu — dan selisih yang seharusnya
 * ketahuan menjadi nol setiap hari. Bukti kenapa ini penting: selisih **−Rp89 jt/outlet** lewat
 * begitu saja di Arena sementara kolom alasan terisi **17 dari 8.125 hari**.
 *
 * ⇒ Urutannya: ketik hitungan → kirim → **baru** selisihnya terlihat.
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { api, ApiFailure } from '../api/client';
import { pendingCount } from '../api/outbox';
import { logoutStaff, hasPermission, staffSession } from '../api/session';
import { ShiftReportButton } from './ShiftReportButton';
import { StoreReportButton } from './StoreReportButton';

/** Alasan selisih kas. ⛔ Enum kontrak, bukan teks bebas yang diketik kasir. */
const VARIANCE_REASONS = ['cash_variance', 'wrong_qty', 'system_error', 'other'] as const;

export interface SessionShellProps {
  readonly outletCode: string;
  readonly deviceCode: string;
  readonly businessDate: string;
  readonly shiftId: string | null;
  /**
   * ⭐ `businessDate` ikut naik — layar kasir menampilkan **tanggal usaha SESI**, bukan jam
   * dinding. Transaksi 01:30 milik hari kemarin (§2).
   */
  readonly onSessionChanged: (shiftId: string | null, businessDate?: string) => void;
  /**
   * ⭐⭐ **Kembali ke layar kasir TANPA mengubah apa pun** (Imron, 2026-08-23).
   *
   * ⛔ Sampai kini layar ini adalah ruangan tanpa pintu keluar: satu-satunya tombol selain
   * "Buka Sesi"/"Tutup Sesi" adalah **KELUAR** — yang mengeluarkan stafnya dari terminal. Kasir
   * yang cuma ingin MELIHAT kas seharusnya, atau yang salah ketuk, harus memilih antara membuka
   * sesi yang tak ia inginkan atau logout dari mesinnya sendiri di tengah shift.
   *
   * ⚠️ `undefined` ⇒ tombolnya tak muncul — dipakai saat layar ini memang titik akhir (belum ada
   * kasir di belakangnya untuk dituju).
   */
  readonly onCancel?: () => void;
}

interface CloseResult {
  readonly expectedCash: string;
  readonly countedCash: string;
  readonly variance: string;
  readonly needsApproval: boolean;
  readonly refunds?: { count: number; total: string; cash: string };
  readonly drawerOpens: readonly { reasonCode: string; count: number }[];
}

function formatRupiah(canonical: string): string {
  const bulat = canonical.split('.')[0] ?? '0';
  return `Rp ${bulat.replace(/\B(?=(\d{3})+(?!\d))/g, '.')}`;
}

export function SessionShell(p: SessionShellProps) {
  const { t } = useTranslation();
  const staff = staffSession();
  const [modal, setModal] = useState('0');
  const [counted, setCounted] = useState('');
  const [reason, setReason] = useState<(typeof VARIANCE_REASONS)[number]>('cash_variance');
  /**
   * ⭐⭐ **Uang yang DITAHAN sebagai modal shift berikutnya** (Imron, 2026-08-25).
   * ⛔ Yang DISETOR tak diketik siapa pun — ia turunan `dihitung − ditahan`, dan kolom turunan yang
   * diketik ulang adalah kolom yang kelak berselisih dengan sumbernya.
   */
  const [retained, setRetained] = useState('');
  /**
   * ⭐ Penyelia yang menyaksikan serah-terima. ⚠️ OPSIONAL — warung yang kasirnya pemiliknya
   * sendiri tetap harus bisa menutup sesi. Yang wajib: kalau namanya diisi, PIN-nya juga.
   */
  const [spvCode, setSpvCode] = useState('');
  const [spvPin, setSpvPin] = useState('');
  const [note, setNote] = useState('');
  const [result, setHasil] = useState<CloseResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [settlement, setSettlement] = useState<string | null>(null);
  /**
   * ⭐⭐⭐ **Sesi sudah BENAR-BENAR tertutup** — tinggal lembarnya, lalu pulang (2026-08-25).
   *
   * ⛔ Sebelumnya layar langsung pulang ke "Buka Sesi Kas" begitu sesi tertutup, dan itu membuang
   * dua hal sekaligus: tombol **Tutup Toko** (yang cuma ada di layar sesi berjalan) dan kesempatan
   * **mencetak ulang Z-report** saat kertasnya macet. Padahal server justru menuntut urutan
   * sebaliknya — menutup hari selagi masih ada sesi terbuka butuh persetujuan
   * (`DAY-409-NOT_CLEAN`). ⇒ urutan yang benar tak bisa dijalankan sama sekali.
   */
  const [selesai, setSelesai] = useState(false);
  /** Sesi lain yang masih terbuka di outlet ini — penentu munculnya tawaran Tutup Toko. */
  const [sisaSesi, setSisaSesi] =
    useState<{ today: number; total: number; devices?: readonly string[] } | null>(null);
  /** Penjualan yang belum diakui portal. Settlement DITOLAK selama ini > 0 (lihat komentar di bawah). */
  const [pending, setPending] = useState(() => pendingCount());
  useEffect(() => {
    const id = setInterval(() => { setPending(pendingCount()); }, 3_000);
    return () => { clearInterval(id); };
  }, []);

  const actor = {
    actorKind: 'staff' as const,
    code: staff?.staffCode ?? '',
    name: staff?.displayName ?? '',
  };

  /*
   * ⭐⭐ **Lanjutkan sesi yang sudah terbuka**, jangan memaksa membuka yang baru.
   *
   * Gerbang `SHIFT-409-ALREADY_OPEN` benar — dua sesi di satu laci membuat `expected_cash` tak
   * bisa dijawab. Tapi layar yang selalu mencoba membuka mengubahnya jadi **jebakan**: kasir yang
   * me-reload, atau tabletnya restart di tengah shift, terkunci dari sesinya SENDIRI tanpa jalan
   * keluar selain memanggil orang.
   *
   * ⚠️ Gagal memeriksa TIDAK menghalangi — layar tetap menawarkan buka-sesi, dan servernya yang
   * menolak bila memang sudah ada. Probe yang mati tak boleh ikut mematikan pintunya.
   */
  useEffect(() => {
    if (p.shiftId !== null) return;
    let alive = true;
    void api
      .currentShift(p.outletCode, p.deviceCode)
      .then((r) => {
        if (alive && r.shiftId !== undefined) p.onSessionChanged(r.shiftId, r.businessDate);
      })
      .catch(() => undefined);
    return () => { alive = false; };
  }, [p]);

  /* ── belum ada sesi: buka ─────────────────────────────────────────────── */
  if (p.shiftId === null) {
    return (
      <section className="session" aria-label={t('session.title')} data-testid="session-open">
        <h1>{t('session.open.title')}</h1>

        {/*
          ⭐⭐ **Identitas sesi disebut SEBELUM angkanya diketik** (mockup Imron, 2026-08-24).

          ⛔ Bukan hiasan. Satu outlet boleh punya banyak till (K20), dan sejak 2026-08-24 nomor
          bill pun berprefiks `device_code` — jadi "di mesin mana saya berdiri" adalah pertanyaan
          yang jawabannya muncul di tiap struk sepanjang hari. Kasir yang membuka sesi di till yang
          salah baru tahu saat setoran tak cocok, malam harinya.
        */}
        <dl className="session-id">
          <dt>{t('session.open.businessDate')}</dt>
          {/* ⛔ Hari USAHA dari SERVER, bukan jam tablet: transaksi 01:30 milik hari kemarin (§2). */}
          <dd data-testid="session-bdate">{p.businessDate}</dd>

          <dt>{t('session.open.cashier')}</dt>
          <dd data-testid="session-staff">{staff?.displayName ?? '—'}</dd>

          <dt>{t('session.open.terminal')}</dt>
          <dd data-testid="session-device">{p.deviceCode}</dd>
        </dl>

        <h2 className="session-sub">{t('session.open.floatTitle')}</h2>
        {/*
          ⭐⭐⭐ **Hitungan BUTA juga di pembukaan.** Tak ada angka yang disodorkan lebih dulu —
          tak "modal kemarin", tak "standar outlet". Angka yang terlihat sebelum diketik adalah
          angka yang akan diketik, dan modal yang selalu pas membuat selisih tutup sesi berpindah
          diam-diam ke shift berikutnya. Alasan yang sama dengan `expected_cash` di penutupan.
        */}
        <p className="hint" data-testid="session-float-hint">{t('session.open.floatHint')}</p>

        <label>
          {t('session.open.openingFloat')}
          <input
            type="number"
            inputMode="numeric"
            data-testid="session-float"
            value={modal}
            onChange={(e) => setModal(e.target.value)}
          />
        </label>

        {/*
          ⚠️ **Catatan, bukan alasan ber-enum — dan bedanya sengaja.** `variance_reason_code` di
          penutupan adalah klasifikasi yang dijumlahkan laporan, jadi ia enum (teks bebas di VTEC:
          18% kosong, sisanya `C`/`v`/`copy`). Yang ini tak pernah dijumlahkan; ia konteks untuk
          manusia yang membaca lembar setoran.

          ⛔ Ia juga tak berlagak jadi gerbang: selama outlet belum punya modal awal STANDAR, tak
          ada yang bisa menyatakan sebuah modal "tak sesuai" — jadi labelnya menyebut dirinya
          opsional alih-alih menuntut sesuatu yang tak punya pembanding.
        */}
        <label>
          {t('session.open.note')}
          <input
            type="text"
            data-testid="session-note"
            maxLength={200}
            placeholder={t('session.open.notePlaceholder')}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </label>

        {error !== null && <p className="error" data-testid="session-error">{error}</p>}

        <button
          type="button"
          className="action-primary"
          data-testid="session-open-button"
          disabled={busy}
          onClick={() => {
            setBusy(true);
            setError(null);
            void api
              .openShift({
                outletCode: p.outletCode,
                // ⚠️ Nomor sesi diturunkan dari perangkat + tanggal, bukan diketik kasir: nomor
                // yang diketik akan bentrok, dan bentroknya baru terlihat saat tutup sesi.
                businessDate: p.businessDate,
                deviceCode: p.deviceCode,
                openingFloat: `${modal === '' ? '0' : modal}.0000`,
                ...(note.trim() === '' ? {} : { openingNote: note.trim() }),
                actor: actor,
              })
              .then((r) => { p.onSessionChanged(r.shiftId, r.businessDate); })
              .catch((e: unknown) => {
                /*
                 * ⛔⛔ **Sesi kas BARU tak bisa dibuka luring — dan itu disengaja.**
                 *
                 * Di sinilah gerbang K20 ditegakkan: `SHIFT-403-DEVICE_MAY_NOT_TENDER` diputuskan
                 * SERVER, dari `device.role` yang disetel manajer. Membolehkan tablet membuka
                 * sesinya sendiri saat luring berarti gerbang itu bisa dilewati dengan mencabut
                 * kabel — dan mesin yang bukan till mulai menerima uang.
                 *
                 * ⚠️ Sesi yang SUDAH terbuka tetap jalan (salinannya di `App`): yang ditolak
                 * membuka yang baru, bukan meneruskan yang sudah lolos gerbang saat daring.
                 */
                setError(e instanceof ApiFailure ? e.message : t('session.open.offlineBlocked'));
              })
              .finally(() => { setBusy(false); });
          }}
        >
          {t('session.open.button')}
        </button>

        {/*
          ⭐ BATAL berdiri di ATAS "Keluar", dan itu disengaja: yang paling sering dibutuhkan adalah
          kembali, bukan logout — dan dua tombol berakibat sangat berbeda tak boleh bersebelahan
          tanpa urutan yang memihak yang lebih aman.
        */}
        {p.onCancel !== undefined && (
          <button type="button" data-testid="session-cancel" onClick={p.onCancel}>
            {t('session.cancel')}
          </button>
        )}

        <button type="button" data-testid="logout" onClick={() => { logoutStaff(); p.onSessionChanged(null); }}>
          {t('session.logout')}
        </button>
      </section>
    );
  }

  /* ── sesi SUDAH tertutup: lembar, tawaran tutup toko, lalu pulang ─────── */
  if (selesai) {
    /* ⭐ Ditanya saat panel ini muncul. Jawabannya menentukan apakah orang ini melihat tombol
       yang menutup seluruh hari — jadi ia fakta SERVER, bukan tebakan kasir (K20). */
    if (sisaSesi === null) {
      void api.openShiftCount(p.outletCode, p.businessDate)
        .then((r) => { setSisaSesi(r); })
        /* ⚠️ Gagal bertanya ⇒ dianggap MASIH ADA sesi lain. Menutup hari karena probe yang mati
           adalah kegagalan yang tak bisa dibatalkan; menahannya cuma merepotkan. */
        .catch(() => { setSisaSesi({ today: 1, total: 1 }); });
    }
    const sendirian = sisaSesi !== null && sisaSesi.today === 0;

    return (
      <section className="session" aria-label={t('session.title')} data-testid="session-closed">
        <h1>{t('session.closed.title')}</h1>
        <p className="dialog-note">{t('session.closed.note')}</p>

        {/*
          ⭐⭐ **Cetak ulang Z-report** — inilah alasan layar ini tak boleh langsung pulang.
          Kertas macet saat menutup sesi bukan kejadian langka, dan lembar inilah yang diserahkan
          bersama uangnya. ⚠️ Cetak ulang TAK PERNAH menendang laci (K27).
        */}
        {p.shiftId !== null && <ShiftReportButton shiftId={p.shiftId} kind="z" />}

        {hasPermission('day:close') && (
          sendirian
            ? (
              <div className="settlement" data-testid="settlement">
            <h2>{t('session.settlement.title')}</h2>
            <p className="dialog-note">{t('session.settlement.warning')}</p>
            {/*
              ⛔⛔ **Settlement WAJIB daring — dan alasannya bukan "5G sudah ada".**

              Settlement membekukan angka hari itu, lalu MENOLAK void & buka-kembali. Kalau ia
              dijalankan selagi ada penjualan tertahan di antrean, penjualan itu tiba SESUDAH
              harinya ditutup — dan gerbang `assertDayNotClosed` di server akan menolaknya
              **selamanya**. Hasilnya penjualan yang tak bisa masuk ke mana pun: ditolak portal,
              sudah hilang dari layar, dan uangnya tetap di laci.

              ⇒ Yang menghalangi bukan "internet mati", melainkan "masih ada yang belum diakui".
                Karena itu tombolnya mati saat antrean berisi — bukan saat server terlihat putus.
            */}
            {pending > 0 && (
              <p className="error" data-testid="settlement-blocked">
                {t('session.settlement.pending', { n: pending })}
              </p>
            )}
            {settlement !== null && <p data-testid="settlement-message">{settlement}</p>}
            <button
              type="button"
              data-testid="settlement-button"
              disabled={pending > 0}
              onClick={() => {
                void api
                  .closeBusinessDay({
                    outletCode: p.outletCode, businessDate: p.businessDate, actor: actor,
                  })
                  .then((r) => {
                    setSettlement(t('session.settlement.ok', { shift: r.shiftCount }));
                  })
                  /*
                   * ⚠️ Pesan servernya ditampilkan APA ADANYA — ia sudah menyebut apa yang
                   * menghalangi (berapa pesanan terbuka, berapa sesi terbuka, berapa selisih).
                   * Menggantinya dengan "gagal" menghapus satu-satunya petunjuk yang berguna.
                   */
                  .catch((e: Error) => { setSettlement(e.message); });
              }}
            >
              {t('session.settlement.button')}
            </button>

            {/*
              ⭐⭐⭐ **Lembar akhir hari, di layar yang sama.** Ia terbit dari `business_day` yang
              lahir saat settlement ⇒ satu-satunya momen ia bisa dicetak adalah sesudah tombol di
              atas ditekan. ⛔ Menaruhnya di menu lain berarti bukan "dicetak nanti", melainkan tak
              pernah dicetak — persis nasib Z per sesi sebelum ia dipindah ke sini.
            */}
            <StoreReportButton outletCode={p.outletCode} businessDate={p.businessDate} />
          </div>
            )
            : (
              /* ⛔ Kenapa tawarannya TIDAK ada pun dikatakan: tombol yang hilang tanpa penjelasan
                 terbaca sebagai layar rusak, dan yang berikutnya terjadi adalah orang mencari
                 jalan lain untuk menutup hari. */
              <p className="dialog-note" data-testid="settlement-waiting">
                {sisaSesi === null
                  ? t('session.closed.checking')
                  : (sisaSesi.devices !== undefined && sisaSesi.devices.length > 0
                    ? t('session.closed.othersOpenNamed', {
                        n: sisaSesi.today, tills: sisaSesi.devices.join(', '),
                      })
                    : t('session.closed.othersOpen', { n: sisaSesi.today }))}
              </p>
            )
        )}

        {/*
          ⭐⭐⭐ **Logout di UJUNG, bukan tepat sesudah sesi ditutup.**
          Kalau logout duluan, orang yang mau menutup toko dilempar ke layar PIN dan harus masuk
          lagi cuma untuk itu. ⚠️ Dan ia WAJIB ada: tanpa logout, orang berikutnya membuka sesi
          atas nama kasir sebelumnya — lalu tiap transaksi, tiap buka laci, tiap diskon menempel
          ke nama yang salah.
        */}
        <button
          type="button"
          className="action-primary"
          data-testid="session-finish"
          onClick={() => { logoutStaff(); p.onSessionChanged(null); }}
        >
          {t('session.closed.finish')}
        </button>
      </section>
    );
  }

  /* ── sesi berjalan: tutup ─────────────────────────────────────────────── */
  const zeroVariance = result !== null && Number(result.variance.split('.')[0]) === 0;
  const needsReason = result !== null && !zeroVariance;

  return (
    <section className="session" aria-label={t('session.title')} data-testid="session-close">
      <h1>{t('session.close.title')}</h1>

      {result === null ? (
        <>
          {/*
            * ⭐⭐ HITUNGAN BUTA. Tak ada satu pun angka "seharusnya" di layar ini — kalau ada,
            * kasir akan mengetiknya, dan selisih yang seharusnya ketahuan jadi nol setiap hari.
            */}
          <p className="dialog-note">{t('session.close.blindCount')}</p>
          {/*
            ⭐⭐ **X-report duduk DI ATAS kotak hitungan**, dan urutannya bukan selera: ia dicetak
            SEBELUM uang dihitung, karena gunanya justru jadi lembar tempat menghitung.
            ⛔ Ia tak menumpahkan angka "seharusnya" ke layar sebelum kasir mengetik hitungannya —
            itu tugas `blindCount`. Yang dicetak memang memuatnya, dan bedanya nyata: kertas
            dipegang orang yang sedang menghitung, layar dilihat semua orang yang lewat.
          */}

          <label>
            {t('session.close.countInput')}
            <input
              type="number"
              inputMode="numeric"
              data-testid="session-counted"
              value={counted}
              onChange={(e) => setCounted(e.target.value)}
            />
          </label>

          {/*
            ⭐⭐ **Ditahan vs disetor.** Yang terjadi di laci ada DUA: sebagian jadi modal shift
            berikutnya, sisanya masuk brankas. ⛔ Kotak "disetor" sengaja TIDAK ada — ia dihitung,
            dan angka turunan yang diketik ulang adalah angka yang kelak berselisih.
            ⚠️ Boleh kosong: outlet yang lacinya dikosongkan tiap malam memang tak menahan apa pun,
            dan kosong di sini berarti "tak dinyatakan", bukan nol.
          */}
          <label className="field">
            {t('session.close.retained')}
            <input
              data-testid="session-retained"
              inputMode="numeric"
              value={retained}
              onChange={(e) => setRetained(e.target.value)}
            />
          </label>
          {retained !== '' && counted !== '' && (
            <p className="hint" data-testid="session-deposit">
              {t('session.close.deposit', {
                amount: formatRupiah(`${String(Number(counted) - Number(retained))}.0000`),
              })}
            </p>
          )}

          {/*
            ⭐ **Penyelia** — kolom tanda tangan pada lembar setoran akhirnya punya isi.
            ⛔ Nama TANPA PIN bukan tanda tangan: ia cuma teks yang bisa diketik siapa saja, dan
            lembar yang menyebut nama orang yang tak pernah hadir lebih buruk daripada kolom kosong.
          */}
          <details className="session-witness">
            <summary data-testid="session-witness-toggle">{t('session.close.witness')}</summary>
            <label className="field">
              {t('session.close.witnessCode')}
              <input
                data-testid="session-witness-code"
                value={spvCode}
                onChange={(e) => setSpvCode(e.target.value)}
              />
            </label>
            <label className="field">
              {t('session.close.witnessPin')}
              <input
                data-testid="session-witness-pin"
                type="password"
                inputMode="numeric"
                value={spvPin}
                onChange={(e) => setSpvPin(e.target.value)}
              />
            </label>
          </details>

          {error !== null && <p className="error" data-testid="session-error">{error}</p>}

          {/*
            ⛔⛔⛔ **Sesi kas tak boleh ditutup selagi antrean berisi** — sejak dokumen penjualan
            membawa `shiftId` (dan sebenarnya jauh sebelum itu).

            Dua kerusakan sekaligus, dan keduanya senyap:
            1. `expected_cash` dihitung SERVER dari tender yang sudah sampai. Penjualan yang masih
               di tablet belum sampai ⇒ angka "seharusnya" lebih kecil dari isi laci sungguhan.
               Kasir melihat KELEBIHAN tanpa sebab, dan yang paling mungkin terjadi bukan
               penyelidikan melainkan alasan `cash_variance` yang diketik asal supaya layar lanjut.
            2. Penjualan yang menyusul kemudian membawa `shiftId` sesi yang sudah ditutup ⇒ ditolak
               `ORDER-409-STATE`, dan uangnya berakhir di antrean `needs_review`.

            ⇒ Yang menghalangi bukan "internet mati", melainkan "masih ada yang belum diakui" —
              aturan yang sama persis dengan settlement.
          */}
          {pending > 0 && (
            <p className="error" data-testid="close-blocked">
              {t('session.close.pending', { n: pending })}
            </p>
          )}

          {/*
            ⭐ BATAL juga di layar TUTUP, dan di sini ia lebih penting lagi: menutup sesi tak bisa
            dibatalkan. Kasir yang membuka layar ini untuk melihat berapa yang sudah masuk — atau
            yang salah ketuk — harus punya jalan pulang yang tak menyentuh apa pun.
          */}
          {p.onCancel !== undefined && (
            <button type="button" data-testid="session-cancel" onClick={p.onCancel}>
              {t('session.cancel')}
            </button>
          )}

          <button
            type="button"
            className="action-primary"
            data-testid="session-close-button"
            disabled={counted === '' || busy || pending > 0}
            onClick={() => {
              setBusy(true);
              setError(null);
              void api
                .closeShift(p.shiftId as string, {
                  countedCash: `${counted}.0000`,
                  ...(retained.trim() === '' ? {} : { retainedCash: `${retained}.0000` }),
                  ...(spvCode.trim() === ''
                    ? {}
                    : { witnessStaffCode: spvCode.trim(), witnessPin: spvPin }),
                  actor: actor,
                })
                .then((r) => { setHasil(r); })
                .catch((e: Error) => { setError(e.message); })
                .finally(() => { setBusy(false); });
            }}
          >
            {t('session.close.button')}
          </button>
        </>
      ) : (
        <>
          {/* Baru SESUDAH hitungan dikirim, angkanya dibuka. */}
          <dl data-testid="session-result">
            <dt>{t('session.close.expected')}</dt><dd>{formatRupiah(result.expectedCash)}</dd>
            <dt>{t('session.close.counted')}</dt><dd>{formatRupiah(result.countedCash)}</dd>
            <dt>{t('session.close.variance')}</dt>
            <dd data-testid="session-variance" className={zeroVariance ? '' : 'error'}>
              {formatRupiah(result.variance)}
            </dd>
          </dl>

          {/*
            ⭐⭐⭐ **Z-report muncul BEGITU sesi tertutup** — di layar yang sama, tanpa dicari.
            ⛔ Lembar inilah yang diserahkan bersama uangnya; kalau ia harus dicari di menu lain,
            yang terjadi bukan "dicari nanti" melainkan "tak pernah dicetak", dan setoran berpindah
            tangan tanpa satu lembar pun yang menyebut angkanya.
            ⚠️ Angkanya DIBEKUKAN saat tutup, jadi mencetaknya besok tetap menjawab hal yang sama.
          */}
          {p.shiftId !== null && <ShiftReportButton shiftId={p.shiftId} kind="z" />}

          {/*
            * ⭐ Buka-laci ditampilkan DI SINI, tempat selisih dijelaskan — bukan sebagai laporan
            * terpisah yang tak akan dibuka siapa pun saat sedang menutup sesi.
            */}
          {result.drawerOpens.length > 0 && (
            <ul data-testid="session-drawer">
              {result.drawerOpens.map((d) => (
                <li key={d.reasonCode}>
                  {t(`enum.reasonCode.${d.reasonCode}`)} × {d.count}
                </li>
              ))}
            </ul>
          )}

          {needsReason && (
            <div data-testid="session-reason-block">
              <p className="error">{t('session.close.reasonRequired')}</p>
              <select
                data-testid="session-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value as (typeof VARIANCE_REASONS)[number])}
              >
                {VARIANCE_REASONS.map((a) => (
                  <option key={a} value={a}>{t(`enum.reasonCode.${a}`)}</option>
                ))}
              </select>
              {reason === 'other' && (
                <input
                  data-testid="session-note"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                />
              )}
              <button
                type="button"
                data-testid="session-submit-reason"
                disabled={reason === 'other' && note.trim() === ''}
                onClick={() => {
                  void api
                    .closeShift(p.shiftId as string, {
                      countedCash: result.countedCash,
                      ...(retained.trim() === '' ? {} : { retainedCash: `${retained}.0000` }),
                      ...(spvCode.trim() === ''
                        ? {}
                        : { witnessStaffCode: spvCode.trim(), witnessPin: spvPin }),
                      varianceReasonCode: reason,
                      ...(note.trim() === '' ? {} : { varianceReasonNote: note.trim() }),
                      actor: actor,
                    })
                    /* ⛔ TIDAK pulang ke layar buka-sesi: lihat `selesai`. */
                    .then(() => { setSelesai(true); })
                    .catch((e: Error) => { setError(e.message); });
                }}
              >
                {t('session.close.submitReason')}
              </button>
            </div>
          )}

          {zeroVariance && (
            <button
              type="button"
              className="action-primary"
              data-testid="session-done"
              onClick={() => { setSelesai(true); }}
            >
              {t('session.close.done')}
            </button>
          )}
        </>
      )}

    </section>
  );
}
